/**
 * PreferencesContext - Contexte global pour les préférences utilisateur
 *
 * Système simplifié :
 * - Au LOGIN : charge depuis fichier serveur files/peoples/{uuid}/preferences.json
 * - Au REFRESH : lit depuis localStorage cabazaPreferencesCache
 * - À chaque MODIFICATION : met à jour localStorage ET sauvegarde fichier serveur (async)
 *
 * Préférences gérées :
 * - pinnedItems (modules épinglés)
 * - dashboardDesktops (bureaux du dashboard)
 * - collapsedSidebarGroups (groupes repliés dans la sidebar)
 * - notificationPrefs (préférences de notifications)
 * - selectedThemeId (thème sélectionné)
 * - language (langue de l'interface)
 * - visibleLanguageCodes (langues visibles dans le switcher)
 * - showLanguageSwitcher (afficher/masquer le switcher de langue)
 * - moduleGroups (groupes de modules personnalisés)
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { savePeoplePreferences } from '../services/apiService';
import {
    getUserPreferencesFile,
    saveUserPreferencesFile,
    getDefaultPreferences,
    cleanupOldLocalStorageKeys
} from '../services/filesService';
import { getModulesByProfileTypeAndSubscription } from '../services/tarifsService';
import { getSubscription } from '../services/paymentService';
import { getModuleNavMap } from '../services/modulesService';

const PreferencesContext = createContext(null);

// LocalStorage keys
const PREFERENCES_CACHE_KEY = 'cabazaPreferencesCache';
const ACTIVE_MODULES_KEY = 'cabazaUserActiveModules';

/**
 * Read preferences from localStorage cache
 * @param {string} currentUserId - Current user ID to verify cache belongs to this user
 */
const readPreferencesCache = (currentUserId) => {
    try {
        const cached = localStorage.getItem(PREFERENCES_CACHE_KEY);
        if (cached) {
            const prefs = JSON.parse(cached);
            // Check if cache belongs to current user
            if (prefs._userId && prefs._userId !== currentUserId) {
                console.log('[PreferencesContext] Cache belongs to different user, clearing');
                return null; // Return null to force reload from file
            }
            return { ...getDefaultPreferences(), ...prefs };
        }
    } catch (e) {
        console.error('[PreferencesContext] Error reading preferences cache:', e);
    }
    return null;
};

/**
 * Write preferences to localStorage cache
 * @param {Object} preferences - Preferences to save
 * @param {string} userId - User ID to tag the cache
 */
const writePreferencesCache = (preferences, userId) => {
    try {
        // Add userId to cache to identify owner
        const prefsWithUser = { ...preferences, _userId: userId };
        localStorage.setItem(PREFERENCES_CACHE_KEY, JSON.stringify(prefsWithUser));
    } catch (e) {
        console.error('[PreferencesContext] Error writing preferences cache:', e);
    }
};

/**
 * Read active modules from localStorage
 */
const readActiveModules = () => {
    try {
        const cached = localStorage.getItem(ACTIVE_MODULES_KEY);
        if (cached) {
            return JSON.parse(cached);
        }
    } catch (e) {
        console.error('[PreferencesContext] Error reading active modules:', e);
    }
    return [];
};

/**
 * Write active modules to localStorage
 */
const writeActiveModules = (modules) => {
    try {
        localStorage.setItem(ACTIVE_MODULES_KEY, JSON.stringify(modules));
    } catch (e) {
        console.error('[PreferencesContext] Error writing active modules:', e);
    }
};

/**
 * Get active module codes from localStorage
 */
export const getUserActiveModuleCodes = () => {
    const activeModules = readActiveModules();
    return activeModules.map(m => (m.code || m.CODE || '').toUpperCase());
};

/**
 * Get active modules from localStorage
 */
export const getUserActiveModules = () => {
    return readActiveModules();
};

/**
 * Check if a module is active for the current user
 * @param {string} moduleCode - Module code (e.g., 'CONTACTS')
 * @returns {boolean} True if the module is active
 */
export const isModuleActiveForUser = (moduleCode) => {
    const activeModuleCodes = getUserActiveModuleCodes();
    return activeModuleCodes.includes(moduleCode?.toUpperCase());
};

export const PreferencesProvider = ({ children }) => {
    const userId = parseInt(localStorage.getItem('userId'), 10);
    const userUuid = localStorage.getItem('userUuid');

    // Initialize from localStorage cache (only if same user)
    const [preferences, setPreferences] = useState(() => {
        const cached = readPreferencesCache(userUuid);
        return cached || getDefaultPreferences();
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const loadingRef = useRef(false);
    const saveTimeoutRef = useRef(null);
    const filesSaveTimeoutRef = useRef(null);

    /**
     * Save preferences to localStorage and file server (debounced)
     */
    const savePreferencesToFile = useCallback((prefs) => {
        if (!userUuid) return;

        // Debounce file save (2 seconds)
        if (filesSaveTimeoutRef.current) {
            clearTimeout(filesSaveTimeoutRef.current);
        }

        filesSaveTimeoutRef.current = setTimeout(async () => {
            try {
                await saveUserPreferencesFile(userUuid, prefs);
                console.log('[PreferencesContext] Preferences saved to file');
            } catch (error) {
                console.error('[PreferencesContext] Error saving preferences to file:', error);
            }
        }, 2000);
    }, [userUuid]);

    /**
     * Load user's active modules based on subscription
     */
    const loadUserActiveModules = useCallback(async () => {
        try {
            const profileTypeId = localStorage.getItem('userProfileTypeId');
            const currentUserUuid = localStorage.getItem('userUuid');

            if (!currentUserUuid || !profileTypeId) {
                console.log('[PreferencesContext] Missing userUuid or profileTypeId');
                return [];
            }

            // Get user's subscription from server
            let subscriptionType = 'FREE';
            try {
                const subscription = await getSubscription(currentUserUuid);
                subscriptionType = (subscription?.subscriptionType || subscription?.tarifCode || 'FREE').toUpperCase();
            } catch (subError) {
                console.log('[PreferencesContext] No subscription found, using FREE');
            }

            // Fetch modules from DB
            const activeModules = await getModulesByProfileTypeAndSubscription(
                parseInt(profileTypeId, 10),
                subscriptionType
            );

            console.log(`[PreferencesContext] Loaded ${activeModules?.length || 0} active modules for subscription ${subscriptionType}`);

            // Save to localStorage
            writeActiveModules(activeModules || []);

            // Dispatch event so components can refresh
            window.dispatchEvent(new CustomEvent('userActiveModulesChanged'));

            return activeModules || [];
        } catch (e) {
            console.error('[PreferencesContext] Error loading user active modules:', e);
            return [];
        }
    }, []);

    /**
     * Load preferences at login (from file server)
     */
    const loadPreferencesFromFile = useCallback(async () => {
        if (!userUuid || loadingRef.current) return;

        loadingRef.current = true;
        setIsLoading(true);

        try {
            console.log('[PreferencesContext] Loading preferences from file for:', userUuid);

            // Load active modules first (needed for default pinned items)
            const activeModules = await loadUserActiveModules();

            // Load from file server
            const filePrefs = await getUserPreferencesFile(userUuid);

            let finalPrefs;
            if (filePrefs) {
                // Merge with defaults to ensure all keys exist
                finalPrefs = { ...getDefaultPreferences(), ...filePrefs };
                console.log('[PreferencesContext] Loaded preferences from file');
            } else {
                // No file found - FIRST LOGIN: create preferences with all active modules pinned
                console.log('[PreferencesContext] No preferences file, creating with all active modules pinned');
                const defaults = getDefaultPreferences();

                // Create pinned items for all active modules
                const navMap = getModuleNavMap();
                const pinnedItems = (activeModules || []).map(m => {
                    const code = (m.code || m.CODE || '').toUpperCase();
                    const navId = navMap[code] || m.navid || m.NAVID || code.toLowerCase();
                    return { type: 'module', id: navId };
                });

                // Create dashboard desktops with module identifiers
                const dashboardDesktops = [pinnedItems.map(item => `module:${item.id}`)];

                finalPrefs = {
                    ...defaults,
                    pinnedItems,
                    dashboardDesktops
                };

                // Save to file server for future logins
                try {
                    await saveUserPreferencesFile(userUuid, finalPrefs);
                    console.log('[PreferencesContext] Created and saved initial preferences file');
                } catch (saveError) {
                    console.error('[PreferencesContext] Failed to save initial preferences:', saveError);
                }
            }

            // Save to localStorage cache (tagged with user)
            writePreferencesCache(finalPrefs, userUuid);
            setPreferences(finalPrefs);

            // Clean up old localStorage keys (migration)
            cleanupOldLocalStorageKeys();

            setIsLoaded(true);
        } catch (error) {
            console.error('[PreferencesContext] Error loading preferences from file:', error);
            // Fallback to defaults
            const defaults = getDefaultPreferences();
            writePreferencesCache(defaults, userUuid);
            setPreferences(defaults);
            setIsLoaded(true);
        } finally {
            loadingRef.current = false;
            setIsLoading(false);
        }
    }, [userUuid, loadUserActiveModules]);

    /**
     * Load preferences at refresh (from localStorage only if same user)
     */
    const loadPreferencesFromCache = useCallback(() => {
        const cachedPrefs = readPreferencesCache(userUuid);
        if (cachedPrefs) {
            setPreferences(cachedPrefs);
            setIsLoaded(true);
            console.log('[PreferencesContext] Loaded preferences from cache');
            // Still check if subscription/modules changed
            loadUserActiveModules();
        } else {
            // Cache is for different user or empty, load from file
            console.log('[PreferencesContext] Cache invalid, loading from file');
            loadPreferencesFromFile();
        }
    }, [userUuid, loadUserActiveModules, loadPreferencesFromFile]);

    /**
     * Save preferences (update localStorage and file)
     */
    const savePreferences = useCallback((updates) => {
        if (!userId) return;

        // Update local state immediately
        setPreferences(prev => {
            const newPrefs = { ...prev, ...updates };
            // Save to localStorage immediately (tagged with user)
            writePreferencesCache(newPrefs, userUuid);
            // Save to file (debounced)
            savePreferencesToFile(newPrefs);
            return newPrefs;
        });

        // Also save to DB (debounced) for backward compatibility
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(async () => {
            try {
                await savePeoplePreferences(userId, updates);
            } catch (error) {
                console.error('[PreferencesContext] Error saving preferences to DB:', error);
            }
        }, 500);
    }, [userId, savePreferencesToFile]);

    /**
     * Update pinned items
     */
    const updatePinnedItems = useCallback((pinnedItems) => {
        savePreferences({ pinnedItems });
        window.dispatchEvent(new CustomEvent('pinnedItemsChanged'));
    }, [savePreferences]);

    /**
     * Update dashboard desktops
     */
    const updateDashboardDesktops = useCallback((dashboardDesktops) => {
        savePreferences({ dashboardDesktops });
    }, [savePreferences]);

    /**
     * Update collapsed sidebar groups
     */
    const updateCollapsedSidebarGroups = useCallback((collapsedSidebarGroups) => {
        savePreferences({ collapsedSidebarGroups });
    }, [savePreferences]);

    /**
     * Update notification preferences
     */
    const updateNotificationPrefs = useCallback((notificationPrefs) => {
        savePreferences({ notificationPrefs });
    }, [savePreferences]);

    /**
     * Toggle a single notification preference
     */
    const toggleNotificationPref = useCallback((moduleKey, type) => {
        setPreferences(prev => {
            const newNotificationPrefs = {
                ...prev.notificationPrefs,
                [moduleKey]: {
                    ...(prev.notificationPrefs[moduleKey] || { app: true, email: true }),
                    [type]: !(prev.notificationPrefs[moduleKey]?.[type] ?? true)
                }
            };
            savePreferences({ notificationPrefs: newNotificationPrefs });
            return { ...prev, notificationPrefs: newNotificationPrefs };
        });
    }, [savePreferences]);

    /**
     * Update selected theme ID
     */
    const updateSelectedThemeId = useCallback((selectedThemeId) => {
        savePreferences({ selectedThemeId });
        window.dispatchEvent(new CustomEvent('themeIdChanged', { detail: { selectedThemeId } }));
    }, [savePreferences]);

    /**
     * Update language
     */
    const updateLanguage = useCallback((language) => {
        savePreferences({ language });
        document.documentElement.lang = language;
        window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language } }));
    }, [savePreferences]);

    /**
     * Update visible language codes
     */
    const updateVisibleLanguageCodes = useCallback((visibleLanguageCodes) => {
        const currentLang = preferences.language;
        const finalCodes = visibleLanguageCodes.includes(currentLang)
            ? visibleLanguageCodes
            : [...visibleLanguageCodes, currentLang];
        savePreferences({ visibleLanguageCodes: finalCodes });
    }, [savePreferences, preferences.language]);

    /**
     * Update show language switcher
     */
    const updateShowLanguageSwitcher = useCallback((showLanguageSwitcher) => {
        savePreferences({ showLanguageSwitcher });
    }, [savePreferences]);

    /**
     * Update activities filters (visible modules)
     */
    const updateActivitiesFilters = useCallback((activitiesFilters) => {
        savePreferences({ activitiesFilters });
    }, [savePreferences]);

    /**
     * Update dock visible
     */
    const updateDockVisible = useCallback((dockVisible) => {
        savePreferences({ dockVisible });
    }, [savePreferences]);

    /**
     * Update dock size
     */
    const updateDockSize = useCallback((dockSize) => {
        savePreferences({ dockSize });
    }, [savePreferences]);

    /**
     * Update dock position
     */
    const updateDockPosition = useCallback((dockPosition) => {
        savePreferences({ dockPosition });
    }, [savePreferences]);

    /**
     * Update module groups
     */
    const updateModuleGroups = useCallback((moduleGroups) => {
        savePreferences({ moduleGroups });
        window.dispatchEvent(new CustomEvent('moduleGroupsChanged'));
    }, [savePreferences]);

    /**
     * Refresh module groups from API
     */
    const refreshModuleGroups = useCallback(async () => {
        // Module groups are now stored in preferences, not fetched from API
        // This function is kept for backward compatibility but does nothing
        console.log('[PreferencesContext] refreshModuleGroups called (no-op in new system)');
    }, []);

    /**
     * Clear preferences (reset to defaults)
     */
    const clearPreferences = useCallback(() => {
        const defaults = getDefaultPreferences();
        writePreferencesCache(defaults, userUuid);
        writeActiveModules([]);
        setPreferences(defaults);
        setIsLoaded(false);
    }, [userUuid]);

    // Load preferences on mount (from cache for refresh, or from file if different user)
    useEffect(() => {
        if (userId && !isLoaded && !loadingRef.current) {
            loadPreferencesFromCache();
        }
    }, [userId, isLoaded, loadPreferencesFromCache]);

    // Load from file on login event - ALWAYS load from file on login to ensure fresh data
    useEffect(() => {
        const handleLogin = () => {
            console.log('[PreferencesContext] userLoggedIn event received, forcing load from file');
            // Reset loading ref to allow fresh load
            loadingRef.current = false;
            setIsLoaded(false);
            // Small delay to ensure localStorage has been updated with new user info
            setTimeout(() => {
                loadPreferencesFromFile();
            }, 100);
        };

        window.addEventListener('userLoggedIn', handleLogin);
        return () => window.removeEventListener('userLoggedIn', handleLogin);
    }, [loadPreferencesFromFile]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            if (filesSaveTimeoutRef.current) {
                clearTimeout(filesSaveTimeoutRef.current);
            }
        };
    }, []);

    const value = {
        // State
        preferences,
        isLoading,
        isLoaded,

        // Individual preferences (for convenience)
        pinnedItems: preferences.pinnedItems,
        dashboardDesktops: preferences.dashboardDesktops,
        collapsedSidebarGroups: preferences.collapsedSidebarGroups,
        notificationPrefs: preferences.notificationPrefs,
        selectedThemeId: preferences.selectedThemeId,
        // Language prefs exposed only for logged-in users (LanguageContext reads these)
        // No need to wait for isLoaded: preferences are initialized synchronously from localStorage cache
        userLanguage: (userId && preferences.language) ? preferences.language : null,
        userVisibleLanguageCodes: (userId && preferences.visibleLanguageCodes?.length > 0) ? preferences.visibleLanguageCodes : null,
        showLanguageSwitcher: preferences.showLanguageSwitcher,
        moduleGroups: preferences.moduleGroups,
        dockVisible: preferences.dockVisible !== undefined ? preferences.dockVisible : true,
        dockSize: preferences.dockSize || 'large',
        dockPosition: preferences.dockPosition || 'bottom',
        activitiesFilters: preferences.activitiesFilters || null,

        // Actions
        loadPreferences: loadPreferencesFromFile,
        savePreferences,
        updatePinnedItems,
        updateDashboardDesktops,
        updateCollapsedSidebarGroups,
        updateNotificationPrefs,
        toggleNotificationPref,
        updateSelectedThemeId,
        updateLanguage,
        updateVisibleLanguageCodes,
        updateShowLanguageSwitcher,
        updateActivitiesFilters,
        updateDockVisible,
        updateDockSize,
        updateDockPosition,
        updateModuleGroups,
        refreshModuleGroups,
        loadUserActiveModules,
        clearPreferences,

        // Helpers
        getDefaultNotificationPrefs: () => getDefaultPreferences().notificationPrefs
    };

    return (
        <PreferencesContext.Provider value={value}>
            {children}
        </PreferencesContext.Provider>
    );
};

/**
 * Hook to use preferences context
 */
export const usePreferences = () => {
    const context = useContext(PreferencesContext);
    if (!context) {
        throw new Error('usePreferences must be used within a PreferencesProvider');
    }
    return context;
};

export default PreferencesContext;
